import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { X, Search, Check, AlertCircle } from 'lucide-react';
import './EventMessages.css';

const NewICConversationModal = ({ eventId, onClose, onStartConversation }) => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [requesterCompanyId, setRequesterCompanyId] = useState(null);

    useEffect(() => {
        fetchContacts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    const fetchContacts = async () => {
        try {
            setLoading(true);
            const response = await apiService.getContactsForNewChat(eventId);
            if (response.data.success) {
                setCompanies(response.data.companies || []);
                setRequesterCompanyId(response.data.requester_company_id);
            } else {
                setError('Failed to load contacts');
            }
        } catch (err) {
            console.error('Error fetching contacts:', err);
            setError('Failed to load contacts');
        } finally {
            setLoading(false);
        }
    };

    // Get the selected company IDs (excluding own company)
    const getSelectedCompanyIds = () => {
        const companyIds = new Set();
        selectedContacts.forEach(contact => {
            if (contact.company_id !== requesterCompanyId) {
                companyIds.add(contact.company_id);
            }
        });
        return companyIds;
    };

    // Check if a contact can be selected based on company rules
    const canSelectContact = (contact) => {
        if (selectedContacts.find(c => c.contact_id === contact.contact_id)) {
            return true; // Already selected, can deselect
        }

        const selectedCompanyIds = getSelectedCompanyIds();

        // If contact is from own company, always allow
        if (contact.company_id === requesterCompanyId) {
            return true;
        }

        // If no external company selected yet, allow
        if (selectedCompanyIds.size === 0) {
            return true;
        }

        // If contact is from the same external company already selected, allow
        if (selectedCompanyIds.has(contact.company_id)) {
            return true;
        }

        // Otherwise, can't select (would be a third company)
        return false;
    };

    const toggleContact = (contact, companyName) => {
        if (!canSelectContact(contact)) {
            return;
        }

        const isSelected = selectedContacts.find(c => c.contact_id === contact.contact_id);
        if (isSelected) {
            setSelectedContacts(prev => prev.filter(c => c.contact_id !== contact.contact_id));
        } else {
            // Enrich contact with company name for use when creating chat
            const enrichedContact = {
                ...contact,
                company_name: companyName
            };
            setSelectedContacts(prev => [...prev, enrichedContact]);
        }
    };

    const handleStartConversation = () => {
        if (selectedContacts.length === 0) return;
        onStartConversation(selectedContacts);
    };

    // Filter companies and contacts based on search
    const filteredCompanies = companies.map(company => ({
        ...company,
        contacts: company.contacts.filter(contact => {
            if (!searchTerm) return true;
            const term = searchTerm.toLowerCase();
            return (
                contact.first_name?.toLowerCase().includes(term) ||
                contact.last_name?.toLowerCase().includes(term) ||
                `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(term) ||
                contact.job_title?.toLowerCase().includes(term) ||
                company.company_name?.toLowerCase().includes(term)
            );
        })
    })).filter(company => company.contacts.length > 0);

    const selectedCompanyIds = getSelectedCompanyIds();
    const hasExternalCompanySelected = selectedCompanyIds.size > 0;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>New Conversation</h2>
                    <button className="modal-close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="modal-body">
                    {/* Search */}
                    <div className="search-input-container">
                        <Search size={18} className="search-icon" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by name, title, or company..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ paddingLeft: '40px' }}
                        />
                    </div>

                    {/* Company restriction notice - commented out for now
                    {hasExternalCompanySelected && (
                        <div className="company-restriction-notice">
                            <AlertCircle size={16} />
                            <span>
                                You can only add contacts from your own organization and one other organization.
                            </span>
                        </div>
                    )}
                    */}

                    {/* Selected contacts */}
                    {selectedContacts.length > 0 && (
                        <div className="selected-contacts">
                            {selectedContacts.map(contact => (
                                <div key={contact.contact_id} className="selected-contact-tag">
                                    <span>{contact.first_name} {contact.last_name}</span>
                                    <button onClick={() => toggleContact(contact)}>
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Contacts list grouped by company */}
                    <div className="contacts-list">
                        {loading ? (
                            <div className="messages-loading">
                                <div className="spinner"></div>
                            </div>
                        ) : error ? (
                            <div className="contacts-error">{error}</div>
                        ) : filteredCompanies.length === 0 ? (
                            <div className="contacts-empty">No contacts found</div>
                        ) : (
                            filteredCompanies.map(company => (
                                <div key={company.company_id} className="company-group">
                                    <div className="company-group-header">
                                        <span className="company-group-name">
                                            {company.company_name}
                                            {company.is_own_company && (
                                                <span className="own-company-badge">Your Company</span>
                                            )}
                                        </span>
                                        <span className="company-contact-count">
                                            {company.contacts.length} contact{company.contacts.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    {company.contacts.map(contact => {
                                        const isSelected = selectedContacts.find(c => c.contact_id === contact.contact_id);
                                        const canSelect = canSelectContact(contact);

                                        return (
                                            <div
                                                key={contact.contact_id}
                                                className={`contact-item ${isSelected ? 'selected' : ''} ${!canSelect ? 'disabled' : ''}`}
                                                onClick={() => canSelect && toggleContact(contact, company.company_name)}
                                            >
                                                <div className="contact-avatar">
                                                    {contact.image_url ? (
                                                        <img src={contact.image_url} alt="" />
                                                    ) : (
                                                        `${contact.first_name?.[0] || ''}${contact.last_name?.[0] || ''}`
                                                    )}
                                                </div>
                                                <div className="contact-details">
                                                    <div className="contact-name">
                                                        {contact.first_name} {contact.last_name}
                                                    </div>
                                                    {contact.job_title && (
                                                        <div className="contact-company">
                                                            {contact.job_title}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="contact-checkbox">
                                                    {isSelected && <Check size={14} />}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="modal-btn secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="modal-btn primary"
                        onClick={handleStartConversation}
                        disabled={selectedContacts.length === 0}
                    >
                        {selectedContacts.length > 0 ? 'Start Conversation' : 'Select Contacts'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewICConversationModal;
